import type { NodeType } from 'prosemirror-model';
import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import type { EditorState, Transaction } from 'prosemirror-state';
import { TextSelection } from 'prosemirror-state';

export class ListService {
  constructor(
    protected listNodeType: NodeType,
    protected itemNodeType: NodeType,
    protected paragraphNodeType: NodeType,
  ) {}

  public isActive(state: EditorState) {
    const { $from, $to } = state.selection;

    const range = $from.blockRange($to, node => node.type.name === this.listNodeType.name);
    if (!range) return false;

    return this.listNodeType.name === range.parent.type.name;
  }

  public toggle(getState: () => EditorState, dispatch?: (tr: Transaction) => void) {
    const isActive = this.isActive(getState());
    return isActive
      ? liftListItem(this.itemNodeType)(getState(), dispatch)
      : this.enableList(getState, dispatch);
  }

  private enableList(getState: () => EditorState, dispatch?: (tr: Transaction) => void): boolean {
    let stateRef = getState();
    const wrapCommand = wrapInList(this.listNodeType);
    const canWrap = wrapCommand(stateRef);
    if (canWrap) {
      return wrapCommand(stateRef, dispatch);
    }
    stateRef = this.convertToParagraphs(getState, dispatch);
    return wrapCommand(stateRef, dispatch);
  }

  // method needs to pull current state - it dispatches two transactions separately
  private convertToParagraphs(
    getState: () => EditorState,
    dispatch?: (tr: Transaction) => void,
  ): EditorState {
    let stateRef = getState();
    let { tr } = stateRef;
    const { selection } = stateRef;
    const { anchor, head, from, to } = selection;
    tr.setBlockType(from, to, this.paragraphNodeType);
    dispatch && dispatch(tr);
    stateRef = getState();
    tr = stateRef.tr;
    // selection is lost after setBlockType. Reapplying for wrapInList to work
    const newSelection = new TextSelection(
      stateRef.doc.resolve(anchor),
      stateRef.doc.resolve(head),
    );
    // setBlockType and setSelection can't be chained, I need to dispatch them separately
    tr.setSelection(newSelection);
    dispatch && dispatch(tr);
    return getState();
  }
}
